import type { Action, CatalogContext, ConfigState, PricingOverride } from "./types";

/**
 * Pure reducer for the configuration state machine.
 * All state is immutable — every action returns a new state object.
 * Uses `never` exhaustive default to catch missing action types at compile time.
 *
 * The third `context` parameter is mandatory — it is used by APPLY_DEFAULTS to
 * populate selectedItems from the catalog's defaultItemIds.
 */
export function configurationReducer(state: ConfigState, action: Action, context: CatalogContext): ConfigState {
  switch (action.type) {
    case "TOGGLE_ITEM": {
      const next = new Set(state.selectedItems);
      if (next.has(action.id)) {
        next.delete(action.id);
      } else {
        next.add(action.id);
      }
      return { ...state, selectedItems: next };
    }

    case "ADD_ITEMS": {
      const next = new Set(state.selectedItems);
      for (const id of action.ids) next.add(id);
      return { ...state, selectedItems: next };
    }

    case "SET_SELECTED_ITEMS": {
      return {
        ...state,
        selectedItems: new Set(action.ids),
      };
    }

    case "APPLY_DEFAULTS": {
      return { ...state, selectedItems: new Set(context.defaultItemIds) };
    }

    case "SET_PRICING_OVERRIDE": {
      const next = new Map(state.pricingOverrides);
      next.set(action.id, action.override);
      return { ...state, pricingOverrides: next };
    }

    case "CLEAR_PRICING_OVERRIDE": {
      const next = new Map(state.pricingOverrides);
      next.delete(action.id);
      return { ...state, pricingOverrides: next };
    }

    case "APPLY_BULK_DISCOUNT": {
      // Audit D5: bulk discount preserves user-applied per-item overrides.
      // An override is "user-applied" when:
      //   - the current pricingOverrides has an entry for the item, AND
      //   - that entry differs from originalSnapshot.pricingOverrides[id]
      //     (or originalSnapshot is null, meaning there's no CRM baseline
      //     and any current override is user-applied by definition).
      // Untouched CRM-loaded overrides still get the bulk discount —
      // "10% off everything" still means everything the user hasn't tuned.
      //
      // Audit D1-N: rounding now preserves penny precision to match the
      // per-item Discount-% popover, so 29995 * 0.9 produces 26995.5 via
      // both paths. The previous Math.floor truncated to whole units and
      // also broke the popover's discount-mode detection (which compares
      // priceFromPct === currentPrice within 1p tolerance) for bulk
      // overrides — that side-effect resolves D2 once the rounding aligns.
      // BULK-R1: include the root item when it has a priceNet > 0. The
      // wizard tracks the root in local React state and never adds it to
      // state.selectedItems, so the original iteration silently skipped
      // root-level pricing — Subtotal includes it but Discount didn't,
      // leaking a £-loss into the deal total.
      const itemMap = new Map(action.catalog.items.map((i) => [i.id, i]));
      const originalOverrides = state.originalSnapshot?.pricingOverrides ?? {};
      const next = new Map<string, PricingOverride>();
      const idsToProcess = new Set<string>(state.selectedItems);
      const rootItem = action.catalog.rootItem;
      if (rootItem && (rootItem.priceNet ?? 0) > 0) {
        idsToProcess.add(rootItem.id);
      }
      for (const id of idsToProcess) {
        const item = itemMap.get(id);
        if (!item) continue;

        const currentOverride = state.pricingOverrides.get(id);
        const originalOverride = originalOverrides[id];
        const isUserApplied =
          currentOverride !== undefined &&
          (!originalOverride || currentOverride.netPence !== originalOverride.netPence);

        if (isUserApplied) {
          next.set(id, currentOverride);
          continue;
        }

        const catalogPence = item.priceNet ?? 0;
        const netPence =
          Math.round((catalogPence * (10000 - action.percent)) / 100) / 100;
        next.set(id, { netPence });
      }
      return { ...state, pricingOverrides: next };
    }

    case "RESET_PRICING_OVERRIDES": {
      return { ...state, pricingOverrides: new Map() };
    }

    case "LOAD_SNAPSHOT": {
      const { snapshot } = action;
      return {
        ...state,
        selectedItems: new Set(snapshot.selectedItems),
        pricingOverrides: new Map(Object.entries(snapshot.pricingOverrides) as [string, PricingOverride][]),
        originalSnapshot: snapshot,
      };
    }

    case "RESET_TO_SNAPSHOT": {
      if (state.originalSnapshot === null) {
        return state;
      }
      const snapshot = state.originalSnapshot;
      return {
        ...state,
        selectedItems: new Set(snapshot.selectedItems),
        pricingOverrides: new Map(Object.entries(snapshot.pricingOverrides) as [string, PricingOverride][]),
        // Keep originalSnapshot so dirty detection still works
        originalSnapshot: snapshot,
      };
    }

    default: {
      // Exhaustive check
      const _exhaustive: never = action;
      return _exhaustive;
    }
  }
}
